import Select from "react-select";
import SelectField from "../../../../../../components/SelectField";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { selectPortalStyles } from "../constants";

const PurchaseChargeTable = ({
  vendorIdx,
  vendor,
  canEdit,
  chargeCodeOptions = [],
  currencyOptions = [],
  containerOptions = [],
  loadingChargeCodes,
  loadingCurrencies,
  getPurchaseInputRow,
  updatePurchaseInputRow,
  addPurchaseChargeRow,
  handleRemoveItemRow,
  handleUpdateItemRow,
  fetchGstPercent,
  formik,
}) => {
  const inputRow = getPurchaseInputRow(vendorIdx);

  // Editable row renderer for both API and newly added rows
  const renderEditableRow = (item, actualIndex) => {
    // Match charge_code against both value and label to handle API data stored as label
    const matchedChargeOption = chargeCodeOptions.find(
      (c) => c.value === item.charge_code || c.label === item.charge_code,
    );

    const chargeCodeValue = matchedChargeOption
      ? matchedChargeOption.value
      : item.charge_code || "";

    const chargeCodeOptionsWithCurrent =
      !matchedChargeOption && item.charge_code
        ? [
          { value: item.charge_code, label: item.charge_code },
          ...chargeCodeOptions,
        ]
        : chargeCodeOptions;


    const containerStrings = Array.isArray(item.containers)
      ? item.containers
      : (item.containers || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

    const matchedContainerOptions = containerOptions.filter((o) =>
      containerStrings.some(
        (c) =>
          c === o.value?.toString() ||
          c === o.label ||
          o.label?.includes(c) ||
          c.includes(o.label),
      ),
    );

    // If there are container strings that didn't match any option, add them as custom options
    const unmatchedContainers = containerStrings.filter(
      (c) =>
        !containerOptions.some(
          (o) =>
            c === o.value?.toString() ||
            c === o.label ||
            o.label?.includes(c) ||
            c.includes(o.label),
        ),
    );
    const customContainerOptions = unmatchedContainers.map((c) => ({
      value: c,
      label: c,
    }));
    const allContainerOptions =
      customContainerOptions.length > 0
        ? [...customContainerOptions, ...containerOptions]
        : containerOptions;
    const allMatchedContainerValues =
      customContainerOptions.length > 0
        ? [...matchedContainerOptions, ...customContainerOptions]
        : matchedContainerOptions;

    return (
      <tr key={`item-${actualIndex}`}>
        {/* Charge Code */}
        <td>
          {canEdit ? (
            <SelectField
              options={chargeCodeOptionsWithCurrent}
              value={chargeCodeValue}
              onChange={async (val) => {
                const opt = chargeCodeOptions.find((c) => c.value === val);
                const chargeLabel = opt?.label || val;
                const extra = {};
                // Only update sac_code if the selected option provides one
                if (opt?.sac_code) {
                  extra.sac_code = opt.sac_code;
                }
                // Fetch GST %: apply the value if GST Applicable is "Yes",
                // otherwise set 0.
                const taxPct = await fetchGstPercent(chargeLabel);
                extra.gst_percent = item.gst_applicable === "Yes" ? taxPct : 0;

                handleUpdateItemRow(
                  vendorIdx,
                  actualIndex,
                  "charge_code",
                  chargeLabel,
                  extra,
                );
              }}
              isLoading={loadingChargeCodes}
              searchable
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              isSearchable
            />
          ) : (
            item.charge_code
          )}
        </td>

        {/* SAC Code */}
        <td>
          {canEdit ? (
            <input
              className="form-control form-control-sm"
              placeholder="SAC Code"
              value={item.sac_code || ""}
              onChange={(e) =>
                handleUpdateItemRow(vendorIdx, actualIndex, "sac_code", e.target.value)
              }
            />
          ) : (
            item.sac_code
          )}
        </td>

        {/* Currency */}
        <td className="minselect">
          {canEdit ? (
            <Select
              options={currencyOptions}
              value={
                currencyOptions.find((c) => c.value === item.currency) || null
              }
              onChange={(selected) =>
                handleUpdateItemRow(
                  vendorIdx,
                  actualIndex,
                  "currency",
                  selected?.value || "",
                )
              }
              isLoading={loadingCurrencies}
              placeholder="Select"
              menuPortalTarget={document.body}
              styles={{
                ...selectPortalStyles,
                menu: (base) => ({
                  ...base,
                  minWidth: 120,
                  width: "max-content",
                }),
              }}
            />
          ) : (
            item.currency
          )}
        </td>

        {/* Rate */}
        <td>
          {canEdit ? (
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Rate"
              value={item.rate || ""}
              onChange={(e) =>
                handleUpdateItemRow(vendorIdx, actualIndex, "rate", e.target.value)
              }
            />
          ) : (
            item.rate
          )}
        </td>

        {/* Quantity */}
        <td>
          {canEdit ? (
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Quantity"
              value={item.quantity || ""}
              onChange={(e) =>
                handleUpdateItemRow(vendorIdx, actualIndex, "quantity", e.target.value)
              }
            />
          ) : (
            item.quantity
          )}
        </td>

        {/* Taxable Amount (auto-calculated, read-only) */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={item.taxable_amount || ""}
            readOnly
          />
        </td>

        {/* GST Applicable */}
        <td className="minselect">
          {canEdit ? (
            <Select
              options={[
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
              ]}
              value={
                item.gst_applicable === "No"
                  ? { label: "No", value: "No" }
                  : { label: "Yes", value: "Yes" }
              }
              onChange={async (selected) => {
                const newVal = selected?.value || "Yes";
                // On "Yes", re-fetch GST % from the row's current charge code.
                if (newVal === "Yes") {
                  const taxPct = await fetchGstPercent(item.charge_code);
                  handleUpdateItemRow(
                    vendorIdx,
                    actualIndex,
                    "gst_applicable",
                    newVal,
                    { gst_percent: taxPct },
                  );
                } else {
                  handleUpdateItemRow(
                    vendorIdx,
                    actualIndex,
                    "gst_applicable",
                    newVal,
                  );
                }
              }}
              placeholder="Select"
              menuPortalTarget={document.body}
              styles={{
                ...selectPortalStyles,
                menu: (base) => ({
                  ...base,
                  minWidth: 100,
                  width: "max-content",
                }),
              }}
            />
          ) : (
            item.gst_applicable
          )}
        </td>

        {/* GST % */}
        <td>
          {canEdit ? (
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="GST %"
              value={
                item.gst_percent === "" || item.gst_percent == null
                  ? ""
                  : item.gst_percent
              }
              onChange={(e) =>
                handleUpdateItemRow(vendorIdx, actualIndex, "gst_percent", e.target.value)
              }
              disabled={item.gst_applicable === "No"}
            />
          ) : (
            item.gst_percent
          )}
        </td>

        {/* GST Amount (auto-calculated, read-only) */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={
              item.gst_amount === "" || item.gst_amount == null
                ? ""
                : item.gst_amount
            }
            readOnly
          />
        </td>

        {/* Containers */}
        <td style={{ minWidth: 200 }}>
          {canEdit ? (
            <Select
              isMulti
              options={allContainerOptions}
              value={allMatchedContainerValues}
              onChange={(selected) => {
                const containerLabels = (selected || [])
                  .map((s) => s.label)
                  .join(", ");
                handleUpdateItemRow(
                  vendorIdx,
                  actualIndex,
                  "containers",
                  containerLabels,
                );
              }}
              placeholder="Select Container(s)"
              menuPortalTarget={document.body}
              styles={{
                ...selectPortalStyles,
                menu: (base) => ({
                  ...base,
                  minWidth: 220,
                  width: "max-content",
                }),
                valueContainer: (base) => ({
                  ...base,
                  flexWrap: "wrap",
                  maxHeight: 80,
                  overflowY: "auto",
                }),
              }}
            />
          ) : (
            item.containers
          )}
        </td>

        {/* Action */}
        {canEdit && (
          <td className="text-center">
            <DeleteOutlineIcon
              style={{ cursor: "pointer", color: "red", fontSize: 28 }}
              onClick={() => handleRemoveItemRow(vendorIdx, actualIndex)}
            />
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="mb-4 position-relative" style={{ overflowX: "auto" }}>
      <table
        className="table table-bordered tbllbl mb-2 mb-md-3 d-noneicon"
        style={{ minWidth: 1100 }}
      >
        <thead className="table-light">
          <tr>
            <th>Charge Code</th>
            <th>SAC Code</th>
            <th>Currency</th>
            <th>Rate</th>
            <th>Quantity</th>
            <th>Taxable Amount</th>
            <th>GST Applicable</th>
            <th>GST %</th>
            <th>GST Amount</th>
            <th>Container(s)</th>
            {canEdit && <th className="text-center"></th>}
          </tr>
        </thead>
        <tbody>
          {/* INPUT ROW FOR ADDING NEW CHARGE */}
          {canEdit && (
            <tr>
              <td>
                <SelectField
                  options={chargeCodeOptions}
                  value={inputRow.charge_code}
                  onChange={async (val) => {
                    updatePurchaseInputRow(vendorIdx, "charge_code", val);
                    const opt = chargeCodeOptions.find(
                      (c) => c.value === val,
                    );
                    updatePurchaseInputRow(
                      vendorIdx,
                      "sac_code",
                      opt?.sac_code || "",
                    );
                    // Fetch GST %: apply if GST Applicable is "Yes", else 0.
                    const chargeLabel = opt?.label || val;
                    const taxPct = await fetchGstPercent(chargeLabel);
                    updatePurchaseInputRow(
                      vendorIdx,
                      "gst_percent",
                      inputRow.gst_applicable === "Yes" ? taxPct : 0,
                    );
                  }}
                  isLoading={loadingChargeCodes}
                  searchable
                  menuPortalTarget={document.body}
                  styles={selectPortalStyles}
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm"
                  placeholder="SAC Code"
                  value={inputRow.sac_code || ""}
                  onChange={(e) =>
                    updatePurchaseInputRow(vendorIdx, "sac_code", e.target.value)
                  }
                />
              </td>
              <td className="minselect">
                <Select
                  options={currencyOptions}
                  value={
                    currencyOptions.find(
                      (c) => c.value === inputRow.currency,
                    ) || null
                  }
                  onChange={(selected) =>
                    updatePurchaseInputRow(
                      vendorIdx,
                      "currency",
                      selected?.value || "",
                    )
                  }
                  isLoading={loadingCurrencies}
                  placeholder="Select"
                  menuPortalTarget={document.body}
                  styles={{
                    ...selectPortalStyles,
                    menu: (base) => ({
                      ...base,
                      minWidth: 120,
                      width: "max-content",
                    }),
                  }}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Rate"
                  value={inputRow.rate || ""}
                  onChange={(e) =>
                    updatePurchaseInputRow(vendorIdx, "rate", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Quantity"
                  value={inputRow.quantity || ""}
                  onChange={(e) =>
                    updatePurchaseInputRow(vendorIdx, "quantity", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={inputRow.taxable_amount || ""}
                  readOnly
                />
              </td>
              <td className="minselect">
                <Select
                  options={[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]}
                  value={
                    inputRow.gst_applicable === "No"
                      ? { label: "No", value: "No" }
                      : { label: "Yes", value: "Yes" }
                  }
                  onChange={async (selected) => {
                    const newVal = selected?.value || "Yes";
                    updatePurchaseInputRow(vendorIdx, "gst_applicable", newVal);
                    // On "Yes", re-fetch GST % for the current charge code.
                    if (newVal === "Yes" && inputRow.charge_code) {
                      const opt = chargeCodeOptions.find(
                        (c) => c.value === inputRow.charge_code,
                      );
                      const chargeLabel = opt?.label || inputRow.charge_code;
                      const taxPct = await fetchGstPercent(chargeLabel);
                      updatePurchaseInputRow(vendorIdx, "gst_percent", taxPct);
                    }
                  }}
                  placeholder="Select"
                  menuPortalTarget={document.body}
                  styles={{
                    ...selectPortalStyles,
                    menu: (base) => ({
                      ...base,
                      minWidth: 100,
                      width: "max-content",
                    }),
                  }}
                />
              </td>
              <td>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  // placeholder="GST %"
                  value={inputRow.gst_percent || "0"}
                  onChange={(e) =>
                    updatePurchaseInputRow(
                      vendorIdx,
                      "gst_percent",
                      e.target.value,
                    )
                  }
                  disabled={inputRow.gst_applicable === "No"}
                />
              </td>
              <td>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={inputRow.gst_amount || "0"}
                  readOnly
                />
              </td>
              <td style={{ minWidth: 200 }}>
                <Select
                  isMulti
                  options={containerOptions}
                  value={containerOptions.filter((o) =>
                    (inputRow.containers || []).includes(o.value),
                  )}
                  onChange={(selected) =>
                    updatePurchaseInputRow(
                      vendorIdx,
                      "containers",
                      (selected || []).map((s) => s.value),
                    )
                  }
                  placeholder="Select Container(s)"
                  menuPortalTarget={document.body}
                  styles={{
                    ...selectPortalStyles,
                    menu: (base) => ({
                      ...base,
                      minWidth: 220,
                      width: "max-content",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      maxHeight: 80,
                      overflowY: "auto",
                    }),
                  }}
                />
              </td>
              <td className="text-center">
                <AddCircleOutlineIcon
                  style={{ cursor: "pointer", color: "green", fontSize: 28 }}
                  onClick={() =>
                    addPurchaseChargeRow(vendorIdx, formik, containerOptions)
                  }
                />
              </td>
            </tr>
          )}

          {/* ALL EXISTING ROWS (both API and newly added) - EDITABLE*/}
          {console.log("vendor",vendor)}
          {vendor.items?.map((item, index) => renderEditableRow(item, index))}

        </tbody>
      </table>
    </div>
  );
};

export default PurchaseChargeTable;
